/**
 * inline-size-presets.js — named sizes for inline magnets.
 *
 * WHY PRESETS. An inline card's rendered size is co-owned by two parties that
 * cannot see each other: the customer's page owns the container, the magnet
 * owns a width CAP (the card fills its container up to max-width) and a fixed
 * height. Raw CSS-length inputs asked users a CSS question when they were
 * asking a design question, and invited values that look plausible and render
 * as nothing (a %-height against an auto-height mount collapses the card —
 * found live 2026-08-24). A preset is a named answer to the design question;
 * Custom keeps the full CSS surface behind a disclosure, validated.
 *
 * Presets are pure UI sugar: they resolve to the same mag_inline_width /
 * mag_inline_height the SDK has always read. No schema or SDK change, and any
 * already-stored pair that matches a preset simply shows as that preset.
 */

export const INLINE_SIZE_PRESETS = Object.freeze([
  { id: 'compact',  name: 'Compact',  hint: 'Sidebars and narrow slots',      w: '480px', h: '440px' },
  // Standard stores NOTHING: blank = the SDK's own defaults (640 × min(520px, 80vh)),
  // so a magnet that never touched sizing round-trips untouched.
  { id: 'standard', name: 'Standard', hint: 'The default fit',                w: '',      h: '' },
  { id: 'tall',     name: 'Tall',     hint: 'Longer flows, content columns',  w: '640px', h: 'min(720px, 82vh)' },
  { id: 'hero',     name: 'Hero',     hint: 'The magnet is the page',         w: '100%',  h: 'min(80vh, 860px)' },
]);

/** Thumbnail proportions (of a 56×44 box) so each card previews its own shape. */
export const PRESET_THUMBS = Object.freeze({
  compact:  { w: 26, h: 26 },
  standard: { w: 34, h: 30 },
  tall:     { w: 34, h: 40 },
  hero:     { w: 52, h: 38 },
});

const norm = (v) => String(v || '').trim().replace(/\s+/g, ' ').toLowerCase();

/** Which preset a stored (width, height) pair is, or 'custom'. Blank pair = standard. */
export function matchInlineSizePreset(w, h) {
  const nw = norm(w), nh = norm(h);
  const hit = INLINE_SIZE_PRESETS.find((p) => norm(p.w) === nw && norm(p.h) === nh);
  return hit ? hit.id : 'custom';
}

/** The stored values for a preset id, or null for 'custom' (caller reads the raw fields). */
export function inlineSizeValues(id) {
  const p = INLINE_SIZE_PRESETS.find((x) => x.id === id);
  return p ? { w: p.w, h: p.h } : null;
}

// Mirror of the SDK's cssSize() plus its %-height refusal, so Custom values that
// would silently do nothing are stopped at the form with words instead.
const LENGTH_RX = /^\d+(\.\d+)?(px|rem|em|vh|vw|%)?$/;
const FN_RX = /^(min|max|clamp)\(\s*[\d.\s,a-z%()+*/-]+\)$/i;

/** '' when the value is usable; a human sentence when it is not. `kind` = 'width' | 'height'. */
export function inlineSizeError(value, kind) {
  const s = String(value || '').trim();
  if (!s) return '';
  if (s.length > 40) return 'Keep it under 40 characters.';
  if (!LENGTH_RX.test(s) && !FN_RX.test(s)) {
    return `That isn't a size the widget understands — use a CSS length like 720px or 80vh${kind === 'width' ? ' or 100%' : ''}, or min()/max()/clamp().`;
  }
  if (kind === 'height' && /%\s*$/.test(s)) {
    return 'A percentage height won’t work here — the card would collapse to its content. Use px or vh instead (720px, 80vh).';
  }
  return '';
}

export default { INLINE_SIZE_PRESETS, PRESET_THUMBS, matchInlineSizePreset, inlineSizeValues, inlineSizeError };
